/**
 * Artifact renderer with template delegation
 *
 * Handles common loading/error states and delegates artifact-specific
 * rendering to specialized templates based on artifact kind.
 *
 * Data Contract: The gatherer's select function guarantees that data will be Artifact | null.
 */

import { ERROR, LOADING, OK } from "helpers/data-statuses"
import { getArtifactTemplate } from "./templates"
import type { ArtifactRendererProps } from "connected-components/dashboard/modules/ai-artifact/types"

/**
 * Artifact renderer component
 *
 * Provides common wrapper for loading/error states and delegates
 * to kind-specific templates for actual artifact rendering.
 *
 */
export default function ArtifactRenderer({ status, data, ...rendererProps }: ArtifactRendererProps) {
  if (status === LOADING) {
    return <div className="ai-artifact-module__status">Loading artifact...</div>
  }

  if (status === ERROR) {
    return <div className="ai-artifact-module__error">Unable to load this artifact.</div>
  }

  if (status !== OK) {
    return null
  }

  // Check if we have artifact data
  if (!data) {
    return <div className="ai-artifact-module__empty">No artifact available.</div>
  }

  // Get template for this artifact type
  const template = getArtifactTemplate(data.type)
  if (!template) {
    return <div className="ai-artifact-module__error">Unsupported artifact type.</div>
  }

  // Render using the appropriate template
  const { Component } = template
  return <Component artifact={data} {...rendererProps} />
}
